import { WSButton, WSList, WSText } from "@wingspanhq/fe-component-library";
import { useWSMutation } from "@ws-react-query";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useState } from "react";
import { LoadingPlaceholder } from "../../../components/LoadingPlaceholder";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryUserProfileV2 } from "../../../query/user/queries/useQueryUserProfile";
import {
  useQueryMemberProfile,
  useQueryUserProfile
} from "../../../query/users/queries";
import {
  getCustomerEntity,
  updateCustomerData
} from "../../../services/api/onboarding/customer";
import { performVerification } from "../../../services/api/onboarding/verifications";
import { verificationService } from "../../../shared/services/verificaiton";
import {
  FormBusinessInformation,
  FormDataBusinessInformation
} from "../../Onboarding/components/FormBusinessInformation";
import { getProfileDataBusiness } from "../../Onboarding/components/FormBusinessInformation/getProfileDataBusiness";
import { getProfileDataPerson } from "../../Onboarding/components/FormPartialPerson/getProfileDataPerson";
import {
  FormPersonalInformation,
  FormDataPersonalInformation
} from "../../Onboarding/components/FormPersonalInformation";
import { getTaxCorrectionStep } from "../../Onboarding/steps/CreateAccount/getTaxCorrectionStep";
import {
  getBusinessData,
  getIndividualData
} from "../../Onboarding/steps/CreateAccount/helpers";

type Props = {
  entity: Awaited<ReturnType<typeof getCustomerEntity>>;
  requiredFields: string[];
  onSubmitted: () => Promise<void>;
};

export const TaxCorrectionForm: React.FC<Props> = ({
  entity,
  requiredFields,
  onSubmitted
}) => {
  const userId = useUserId();
  const user = useQueryUserProfile(userId);
  const member = useQueryMemberProfile(userId);
  const userV2 = useQueryUserProfileV2();
  const [submitted, setSubmitted] = useState(false);
  const country = entity.country;
  const step = getTaxCorrectionStep(entity.type);

  const [submit, submitMeta] = useWSMutation(
    async (
      data?: FormDataPersonalInformation | FormDataBusinessInformation
    ) => {
      // A successful submission is not repeated when its follow-up check fails.
      if (!submitted) {
        if (!data || !user.data || !country) {
          throw new Error(
            "Unable to load your information. Please reopen this component to try again."
          );
        }
        const customerData =
          "person" in data
            ? await getIndividualData({
                personFormData: data,
                user: user.data,
                verificationLevel: "Tax"
              })
            : await getBusinessData(data, country, user.data);
        await updateCustomerData("Entity", { customerData });
        await performVerification("Tax");
        setSubmitted(true);
      }
      await onSubmitted();
      setSubmitted(false);
    }
  );

  if (submitted)
    return (
      <WSList gap="L">
        <WSText.ParagraphSm>
          Your correction has been submitted.
        </WSText.ParagraphSm>
        <WSErrorMessage contextKey="MonitoredEntity" error={submitMeta.error} />
        <WSButton loading={submitMeta.isLoading} onClick={() => submit()}>
          Continue
        </WSButton>
      </WSList>
    );

  if (user.isLoading || member.isLoading || userV2.isLoading)
    return <LoadingPlaceholder blank />;
  if (!country || !user.data || member.isError || userV2.isError)
    return (
      <WSErrorMessage
        contextKey="MonitoredEntity"
        error={
          user.error ||
          member.error ||
          userV2.error ||
          "Unable to load your information. Please reopen this component to try again."
        }
      />
    );

  return (
    <WSList gap="L">
      <WSErrorMessage contextKey="MonitoredEntity" error={submitMeta.error} />
      {submitMeta.isLoading && (
        <WSText.ParagraphSm>Submitting your information…</WSText.ParagraphSm>
      )}
      <fieldset
        disabled={submitMeta.isLoading}
        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
      >
        {step === "personal-information" ? (
          <FormPersonalInformation
            country={country}
            defaultValues={{
              person: {
                ...getProfileDataPerson({
                  type: "Individual",
                  user: user.data,
                  member: member.data,
                  userV2: userV2.data
                }),
                country
              }
            }}
            mismatch={verificationService.getIsIndividualMismatch(
              requiredFields
            )}
            onSubmit={submit}
          />
        ) : (
          <FormBusinessInformation
            country={country}
            defaultValues={getProfileDataBusiness(member.data)}
            personalEmail={user.data.email}
            mismatch={verificationService.getIsBusinessMismatch(requiredFields)}
            onSubmit={submit}
          />
        )}
      </fieldset>
    </WSList>
  );
};
